class CollapsibleText {
    #method = 'clamp';
    #keepSession = 'true';
    #item = '[role="listitem"]';
    #parent = undefined;
    #tmpl = undefined;
    #wrapper = undefined;
    #initialState = 'collapse';
    #value = 4;
    #initialized = false;

    #action = {
        COLLAPSE: 'data-label-collapse',
        EXPAND: 'data-label-expand',
        REMAIN: 'data-label-remaining',
    };

    _list = [];
    _lastVisibleItem = undefined;
    _constructedKey = undefined;

    /**
     * @param {object} config - The configuration object.
     * @param {string} [config.method] - whether the collapsing should happen on specific element number or height in pixels
     * @param {boolean} [config.keepSession] - whether the collapsing state should be remembered between page loads
     * @param {string} config.wrapper - the container that wraps the to-be-collapsed elements
     * @param {string} config.templateId the id of the template to be used to create the toggling buttons
     * @param {number} config.value the id of the template to be used to create the toggling buttons
     * @param {string} [config.parent] the Node where we should confine our querySelectors, defaults to document.body
     * @param {string} [config.item] the selector for the items to be collapsed
     */
    constructor(config) {
        this.#method = config.method || this.#method;
        this.#keepSession = config.keepSession || this.#keepSession;
        this.#tmpl = document.getElementById(config.templateId);
        this.#item = config.item || this.#item;
        this.#parent = document.body.querySelector(config.parent || 'article[data-design]');
        this.#wrapper = this.#parent?.querySelector(config.wrapper);
        this.#value = config.value;

        if (!this.#wrapper) {
            console.debug('Collapsible Text: No wrapper found');
            return;
        }

        this._list = this.#wrapper.querySelectorAll(this.#item);

        if (
            this.#method === 'height'
                ? this.#wrapper.getBoundingClientRect().height > this.#value
                : this._list?.length > this.#value
        ) {
            this.init();
        } else {
            console.log('Collapsible Text: No need to collapse', config);
        }
    }

    init() {
        if (!this.#wrapper) return;

        this.#container.init();
        this.#trigger.init();

        this._constructedKey = sessionStorage.getItem('doi') + '_' + this.#wrapper.id + '_expanded';
        this.#initialState =
            this.#keepSession && sessionStorage.getItem(this._constructedKey) === 'true' ? 'expand' : 'collapse';

        this.#container[this.#initialState]();
        this.#trigger[this.#initialState]();
        this.#initialized = true;
    }
    expand() {
        this.#container.expand();
        this.#trigger.expand();
    }
    collapse() {
        this.#container.collapse();
        this.#trigger.collapse();
    }

    #trigger = {
        el: undefined,
        init: () => {
            this.#trigger.el = this.#wrapper.parentElement.querySelector('[data-trigger="collapse"]');
            this.#trigger.el.setAttribute('aria-controls', this.#wrapper.id);
            this.#trigger.el.addEventListener('click', () => {
                this.#trigger.el.getAttribute('aria-expanded') === 'true' ? this.expand() : this.collapse();
            });
        },
        expand: () => {
            const label = this.#trigger.el.getAttribute(this.#action.COLLAPSE);
            this.#trigger.el.setAttribute('aria-label', label);
            this.#trigger.el.setAttribute('aria-expanded', 'false');
            this.#trigger.el.innerHTML = label;
        },
        collapse: () => {
            const label = this.#trigger.el.getAttribute(this.#action.EXPAND);
            const remain = this.#trigger.el.getAttribute(this.#action.REMAIN);
            const number = this.#wrapper.childNodes.length - (this.#method === 'height' ? 0 : this.#value);
            const ariaLabel = label + ' - ' + number + ' ' + remain;
            this.#trigger.el.setAttribute('aria-label', ariaLabel);
            this.#trigger.el.setAttribute('aria-expanded', 'true');
            this.#trigger.el.innerHTML = label;
        },
    };

    #container = {
        init: () => {
            const clone = this.#tmpl.content.cloneNode(true);
            this.#wrapper.parentNode.insertBefore(clone, this.#wrapper.nextSibling);
            if (!this.#wrapper.id) {
                this.#wrapper.id = (this.#wrapper.parentNode.id || 'unlabeled') + '-collapsible-text';
            }
        },
        expand: () => {
            this.#wrapper.removeAttribute('data-method');
            this.#toggleVisibility(true);
            this.#keepSession && sessionStorage.setItem(this._constructedKey, 'true');
            this._lastVisibleItem?.querySelector('a, button, input, select, textarea')?.focus();
        },
        collapse: () => {
            this.#wrapper.setAttribute('data-method', this.#method);
            this.#toggleVisibility(false);
            this.#keepSession && sessionStorage.removeItem(this._constructedKey);
            this._lastVisibleItem = this.#getLastVisibleItem();
            if (this.#initialized) {
                this.#parent.scrollIntoView({behavior: 'smooth'});
                this.#parent.focus();
            }
        },
    };

    #toggleVisibility = visible => {
        const items = Array.from(this._list);
        if (this.#method === 'height') {
            let countedHeight = 0;
            items.slice(items.indexOf(this._lastVisibleItem) + 1).forEach(item => {
                countedHeight += item.getBoundingClientRect().height;
                if (visible) {
                    item.hidden = false;
                } else if (countedHeight > this.#value) {
                    item.hidden = !visible;
                }
            });
        } else if (this.#method === 'clamp') {
            items.slice(this.#value).forEach(item => (item.hidden = !visible));
        }
    };
    #getLastVisibleItem = () => {
        return Array.from(this._list)
            .reverse()
            .find(item => {
                if (this.#method === 'clamp') {
                    return window.getComputedStyle(item).display !== 'none';
                } else {
                    const wrapperRect = this.#wrapper.getBoundingClientRect();
                    const firstInteractiveElement = item.querySelector('a, button, input, select, textarea');
                    if (firstInteractiveElement) {
                        const interactiveRect = firstInteractiveElement.getBoundingClientRect();
                        return interactiveRect.top < wrapperRect.bottom && interactiveRect.bottom > wrapperRect.top;
                    }
                }
            });
    };
}

module.exports = CollapsibleText;
